/**
 * HierarchicalSelectrManager.ts - Hierarchical Selection State Management
 * 
 * CHANGE HISTORY:
 * - 251115_2200 [pending] danielbeer: Create hierarchical selection manager
 * 
 * Extends SelectrManager with hierarchical selection capabilities.
 * Supports generation-aware, family-aware, and tree-based selection.
 */

import { SelectrManager, SelectionOptions, SelectionMode } from './SelectrManager';

export interface HierarchicalNode {
  id: string;
  parentId: string | null;
  generation: number;
  familyId?: string;
  children?: string[];
  [key: string]: any;
}

export interface HierarchicalOptions extends SelectionOptions {
  enforceFamilyRules?: boolean;
  allowCrossFamilySelection?: boolean;
}

export interface ValidationResult {
  valid: boolean;
  reason?: string;
  invalidIds?: string[];
}

export interface BatchContext {
  selectedIds: string[];
  selectedNodes: HierarchicalNode[];
  commonAncestor: string | null;
  minGeneration: number;
  maxGeneration: number;
  families: Set<string>;
}

export interface BatchOperation {
  type: 'delete' | 'move' | 'copy' | 'update';
  targetId?: string;
  data?: any;
}

export interface BatchResult {
  success: boolean;
  affectedIds: string[];
  errors?: string[];
}

/**
 * HierarchicalSelectrManager - Hierarchical selection state management
 * 
 * Extends basic selection with hierarchical awareness:
 * - Generation-based selection (children, siblings, ancestors)
 * - Family-aware rules (can't select across families)
 * - Range selection in tree order
 * - Batch operations on selected nodes
 */
export class HierarchicalSelectrManager extends SelectrManager {
  protected nodes: Map<string, HierarchicalNode> = new Map();
  protected hierarchicalOptions: Required<HierarchicalOptions>;

  constructor(nodes: HierarchicalNode[], options: HierarchicalOptions = {}) {
    super(options);
    
    this.hierarchicalOptions = {
      ...this.options,
      enforceFamilyRules: options.enforceFamilyRules ?? true,
      allowCrossFamilySelection: options.allowCrossFamilySelection ?? false
    };

    this.updateNodes(nodes);
  }

  // ========================================
  // NODE MANAGEMENT
  // ========================================

  /**
   * Update node data
   */
  updateNodes(nodes: HierarchicalNode[]): void {
    this.nodes.clear();
    nodes.forEach(node => this.nodes.set(node.id, node));
  }

  /**
   * Add or update a single node
   */
  updateNode(node: HierarchicalNode): void {
    this.nodes.set(node.id, node);
  }

  /**
   * Remove a node
   */
  removeNode(id: string): void {
    this.nodes.delete(id);
    this.selected.delete(id);
  }

  /**
   * Get node by ID
   */
  getNode(id: string): HierarchicalNode | undefined {
    return this.nodes.get(id);
  }

  // ========================================
  // RANGE SELECTION
  // ========================================

  /**
   * Select range between two nodes (in visual/tree order)
   */
  selectRange(startId: string, endId: string): void {
    const orderedNodes = this.getVisualOrder();
    const startIdx = orderedNodes.findIndex(n => n.id === startId);
    const endIdx = orderedNodes.findIndex(n => n.id === endId);

    if (startIdx === -1 || endIdx === -1) {
      console.warn('Invalid range selection: node not found');
      return;
    }

    const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
    const rangeIds = orderedNodes.slice(from, to + 1).map(n => n.id);

    // Validate family rules
    if (this.hierarchicalOptions.enforceFamilyRules) {
      const validation = this.validateSelection(rangeIds);
      if (!validation.valid) {
        console.warn('Range selection violates family rules:', validation.reason);
        return;
      }
    }

    this.select(rangeIds, 'replace');
  }

  // ========================================
  // HIERARCHICAL SELECTION
  // ========================================

  /**
   * Select all children of a node
   */
  selectChildren(parentId: string, recursive: boolean = false): void {
    const children = this.getChildren(parentId);
    const childIds = children.map(c => c.id);

    if (recursive) {
      const descendants = this.getDescendants(parentId);
      childIds.push(...descendants.map(d => d.id));
    }

    this.select(childIds, 'add');
  }

  /**
   * Select all siblings of a node
   */
  selectSiblings(nodeId: string, includeSelf: boolean = true): void {
    const node = this.nodes.get(nodeId);
    if (!node) return;

    const siblings = this.getSiblings(nodeId);
    const siblingIds = siblings.map(s => s.id);

    if (includeSelf && !siblingIds.includes(nodeId)) {
      siblingIds.push(nodeId);
    }

    this.select(siblingIds, 'add');
  }

  /**
   * Select all nodes at a specific generation
   */
  selectGeneration(generation: number): void {
    const nodesAtGeneration = Array.from(this.nodes.values())
      .filter(node => node.generation === generation)
      .map(node => node.id);

    this.select(nodesAtGeneration, 'replace');
  }

  /**
   * Select all descendants of a node
   */
  selectDescendants(nodeId: string): void {
    const descendants = this.getDescendants(nodeId);
    const descendantIds = descendants.map(d => d.id);
    this.select(descendantIds, 'add');
  }

  /**
   * Select all ancestors of a node
   */
  selectAncestors(nodeId: string, includeSelf: boolean = false): void {
    const ancestors = this.getAncestors(nodeId);
    const ancestorIds = ancestors.map(a => a.id);

    if (includeSelf) {
      ancestorIds.push(nodeId);
    }

    this.select(ancestorIds, 'add');
  }

  /**
   * Select entire family (all nodes with same familyId)
   */
  selectFamily(familyId: string): void {
    const familyNodes = Array.from(this.nodes.values())
      .filter(node => node.familyId === familyId)
      .map(node => node.id);

    this.select(familyNodes, 'replace');
  }

  // ========================================
  // FAMILY-AWARE VALIDATION
  // ========================================

  /**
   * Check if nodes can be selected together (family rules)
   */
  canSelectTogether(ids: string[]): boolean {
    if (!this.hierarchicalOptions.enforceFamilyRules) return true;
    if (this.hierarchicalOptions.allowCrossFamilySelection) return true;

    const families = new Set(
      ids
        .map(id => this.nodes.get(id)?.familyId)
        .filter(Boolean)
    );

    // All selected nodes must be in the same family (or have no family)
    return families.size <= 1;
  }

  /**
   * Validate a selection
   */
  validateSelection(ids: string[]): ValidationResult {
    // Check if all nodes exist
    const invalidIds = ids.filter(id => !this.nodes.has(id));
    if (invalidIds.length > 0) {
      return {
        valid: false,
        reason: 'Some nodes do not exist',
        invalidIds
      };
    }

    // Check family rules
    if (this.hierarchicalOptions.enforceFamilyRules && 
        !this.hierarchicalOptions.allowCrossFamilySelection) {
      if (!this.canSelectTogether(ids)) {
        return {
          valid: false,
          reason: 'Cannot select nodes from different families',
          invalidIds: ids
        };
      }
    }

    return { valid: true };
  }

  /**
   * Get common ancestor of selected nodes
   */
  getCommonAncestor(ids?: string[]): string | null {
    const selectedIds = ids || this.getSelected();
    if (selectedIds.length === 0) return null;
    if (selectedIds.length === 1) return selectedIds[0];

    // Get all ancestors for first node
    const firstAncestors = this.getAncestors(selectedIds[0]);
    const firstAncestorIds = [selectedIds[0], ...firstAncestors.map(a => a.id)];

    // Find first ancestor that's common to all
    for (const ancestorId of firstAncestorIds) {
      const isCommon = selectedIds.every(id =>
        this.isAncestor(ancestorId, id) || ancestorId === id
      );
      if (isCommon) return ancestorId;
    }

    return null;
  }

  // ========================================
  // BATCH OPERATIONS
  // ========================================

  /**
   * Get batch context for selected nodes
   */
  getBatchContext(): BatchContext {
    const selectedIds = this.getSelected();
    const selectedNodes = selectedIds
      .map(id => this.nodes.get(id))
      .filter(Boolean) as HierarchicalNode[];

    const generations = selectedNodes.map(n => n.generation);
    const families = new Set(
      selectedNodes.map(n => n.familyId).filter(Boolean) as string[]
    );

    return {
      selectedIds,
      selectedNodes,
      commonAncestor: this.getCommonAncestor(selectedIds),
      minGeneration: Math.min(...generations),
      maxGeneration: Math.max(...generations),
      families
    };
  }

  /**
   * Execute batch operation on selected nodes
   * Note: Actual operation implementation should be provided by consumer
   */
  executeBatch(operation: BatchOperation): BatchResult {
    const context = this.getBatchContext();
    
    // Validate operation
    if (context.selectedIds.length === 0) {
      return {
        success: false,
        affectedIds: [],
        errors: ['No nodes selected']
      };
    }

    // This is a placeholder - actual implementation depends on use case
    return {
      success: true,
      affectedIds: context.selectedIds,
      errors: []
    };
  }

  // ========================================
  // HELPER METHODS
  // ========================================

  /**
   * Get children of a node
   */
  protected getChildren(parentId: string): HierarchicalNode[] {
    return Array.from(this.nodes.values())
      .filter(node => node.parentId === parentId);
  }

  /**
   * Get siblings of a node
   */
  protected getSiblings(nodeId: string): HierarchicalNode[] {
    const node = this.nodes.get(nodeId);
    if (!node) return [];

    return this.getChildren(node.parentId || '');
  }

  /**
   * Get all descendants of a node (recursive)
   */
  protected getDescendants(nodeId: string): HierarchicalNode[] {
    const descendants: HierarchicalNode[] = [];
    const children = this.getChildren(nodeId);

    for (const child of children) {
      descendants.push(child);
      descendants.push(...this.getDescendants(child.id));
    }

    return descendants;
  }

  /**
   * Get all ancestors of a node
   */
  protected getAncestors(nodeId: string): HierarchicalNode[] {
    const ancestors: HierarchicalNode[] = [];
    let current = this.nodes.get(nodeId);

    while (current?.parentId) {
      const parent = this.nodes.get(current.parentId);
      if (parent) {
        ancestors.push(parent);
        current = parent;
      } else {
        break;
      }
    }

    return ancestors;
  }

  /**
   * Check if one node is ancestor of another
   */
  protected isAncestor(ancestorId: string, descendantId: string): boolean {
    let current = this.nodes.get(descendantId);

    while (current?.parentId) {
      if (current.parentId === ancestorId) return true;
      current = this.nodes.get(current.parentId);
    }

    return false;
  }

  /**
   * Get nodes in visual order (depth-first traversal)
   */
  protected getVisualOrder(): HierarchicalNode[] {
    const result: HierarchicalNode[] = [];

    const traverse = (nodeId: string | null) => {
      const children = this.getChildren(nodeId || '');
      
      // Sort children if they have an order property
      children.sort((a, b) => {
        if ('order' in a && 'order' in b) {
          return (a.order as number) - (b.order as number);
        }
        return 0;
      });

      for (const child of children) {
        result.push(child);
        traverse(child.id);
      }
    };

    traverse(null); // Start from root
    return result;
  }

  /**
   * Get selected nodes (not just IDs)
   */
  getSelectedNodes(): HierarchicalNode[] {
    return this.getSelected()
      .map(id => this.nodes.get(id))
      .filter(Boolean) as HierarchicalNode[];
  }

  /**
   * Export hierarchical state for debugging
   */
  exportState(): any {
    const baseState = super.exportState();
    const context = this.getBatchContext();

    return {
      ...baseState,
      nodeCount: this.nodes.size,
      selectedNodes: context.selectedNodes.length,
      commonAncestor: context.commonAncestor,
      generations: {
        min: context.minGeneration,
        max: context.maxGeneration
      },
      families: Array.from(context.families)
    };
  }
}
